import {
  BusinessIcon,
  FeedbackOutlinedIcon,
  SettingsIcon,
  WorkOutline,
} from '@/ui/primitives/icons';
import { buildWorkspaceSettingsHref } from '../../features/organisations/utils/organisationSettingsRoute';
import { buildProjectSettingsHref } from '../../features/projects/utils/projectSettingsRoute';
import type { NavItem } from '@/components/sidebarTypes';
import type { NotificationContextValue } from '@/providers/notificationContext';
import type { FeatureFlagResolveResult } from '@/shared/data/featureFlags';
import type { SidebarItem, SidebarSection } from '@/ui/composites/SidebarNav';
import type { OrganisationDto, ProjectDto } from '@flowqa/shared';

type BuildSidebarContext = {
  navItems: NavItem[];
  activePath: string;
  onNavigate: (path: string) => void;
  preloadRoute?: (path: string) => Promise<void> | null;
  featureFlags?: FeatureFlagResolveResult['flags'] | null;
  addOns?: {
    deliveryEnabled: boolean;
    operationsEnabled: boolean;
    codeEnabled: boolean;
  } | null;
  isAdmin: boolean;
  isSuperuser: boolean;
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
  resolvedOrganisationId: string | null;
  resolvedProjectId: string | null;
  setSelectedOrganisationId: (value: string | null) => void;
  setSelectedProjectId: (value: string | null) => void;
  notify: NotificationContextValue['notify'];
  projectsOptions: Array<
    Pick<ProjectDto, 'id' | 'name' | 'slug' | 'badgeChars' | 'badgeColor' | 'badgeEnabled'> & {
      locked: boolean;
      lockReason: string | null;
    }
  >;
  allProjectsOptions: Array<
    Pick<ProjectDto, 'id' | 'name' | 'slug' | 'badgeChars' | 'badgeColor' | 'badgeEnabled'> & {
      locked: boolean;
      lockReason: string | null;
    }
  >;
  organisationsOptions: Array<Pick<OrganisationDto, 'id' | 'name' | 'slug'>>;
  canCreateProject: boolean;
  lockProjectSelector: boolean;
  projectLabel: string;
  projectBadge: { chars: string; color: ProjectDto['badgeColor'] } | null;
  projectLocked: boolean;
  onAddProject: () => void;
  onAddOrganisation: () => void;
  onOpenFeedback: () => void;
};

const isSelected = (
  itemPath: string,
  secondaryPath: string | undefined,
  selectedPath: string,
): boolean => {
  if (itemPath === '/') {
    return selectedPath === '/';
  }
  if (selectedPath.startsWith(itemPath)) {
    return true;
  }
  if (secondaryPath && selectedPath.startsWith(secondaryPath)) {
    return true;
  }
  return false;
};

const buildSidebarItem = (
  item: NavItem,
  selectedPath: string,
  preloadRoute: BuildSidebarContext['preloadRoute'],
): SidebarItem => ({
  id: item.key,
  label: item.label,
  href: item.path,
  icon: item.icon,
  active: isSelected(item.path, item.secondaryPath, selectedPath),
  preload: preloadRoute ? (): Promise<void> | null => preloadRoute(item.path) : undefined,
});

const buildSecondaryItem = (
  item: NavItem,
  selectedPath: string,
  preloadRoute: BuildSidebarContext['preloadRoute'],
): SidebarItem | null => {
  const secondaryPath = item.secondaryPath;
  if (!item.secondary || !secondaryPath) return null;
  return {
    id: `${item.key}-secondary`,
    label: item.secondary,
    href: secondaryPath,
    active: isSelected(secondaryPath, undefined, selectedPath),
    preload: preloadRoute ? (): Promise<void> | null => preloadRoute(secondaryPath) : undefined,
  };
};

const pickNavItems = (
  items: NavItem[],
  keys: string[],
  selectedPath: string,
  preloadRoute: BuildSidebarContext['preloadRoute'],
): SidebarItem[] => {
  const byKey = new Map(items.map((item) => [item.key, item]));
  return keys.flatMap((key) => {
    const match = byKey.get(key);
    if (!match) return [];
    const primaryItem = buildSidebarItem(match, selectedPath, preloadRoute);
    const secondaryItem = buildSecondaryItem(match, selectedPath, preloadRoute);
    return secondaryItem ? [primaryItem, secondaryItem] : [primaryItem];
  });
};

export const buildSidebarSections = (
  context: BuildSidebarContext,
): { sections: SidebarSection[]; preFooterItems: SidebarItem[]; footerItems: SidebarItem[] } => {
  const {
    navItems,
    activePath,
    preloadRoute,
    addOns,
    isAdmin,
    isSuperuser,
    resolvedOrganisationId,
    resolvedProjectId,
  } = context;
  const filteredNav = navItems.filter((item) => {
    if (item.requiresSuperuser) {
      return isSuperuser;
    }
    if (item.requiresAdmin) {
      return isAdmin;
    }
    return true;
  });
  const hasResolvedOrganisation = resolvedOrganisationId !== null;
  const deliveryEnabled = !hasResolvedOrganisation || addOns?.deliveryEnabled === true;
  const operationsEnabled = !hasResolvedOrganisation || addOns?.operationsEnabled === true;
  const codeEnabled = !hasResolvedOrganisation || addOns?.codeEnabled === true;
  const canSeeAdminFooter = isAdmin || isSuperuser;
  const feedbackItem: SidebarItem = {
    id: 'feedback',
    label: 'Feedback',
    href: activePath,
    icon: <FeedbackOutlinedIcon fontSize="small" />,
    onClick: (event) => {
      event.preventDefault();
      context.onOpenFeedback();
    },
  };
  const footerItems: SidebarItem[] = [
    ...(canSeeAdminFooter && hasResolvedOrganisation
      ? [
          {
            id: 'workspace-settings',
            label: 'Workspace',
            href: buildWorkspaceSettingsHref(resolvedOrganisationId),
            icon: <BusinessIcon fontSize="small" />,
            active: isSelected('/workspace-settings', undefined, activePath),
            preload: preloadRoute
              ? (): Promise<void> | null => preloadRoute('/workspace-settings')
              : undefined,
          },
        ]
      : []),
    ...(canSeeAdminFooter && resolvedProjectId
      ? [
          {
            id: 'project-settings',
            label: 'Project',
            href: buildProjectSettingsHref(resolvedProjectId),
            icon: <WorkOutline fontSize="small" />,
            active: isSelected('/project-settings', undefined, activePath),
            preload: preloadRoute
              ? (): Promise<void> | null => preloadRoute('/project-settings')
              : undefined,
          },
        ]
      : []),
    ...(canSeeAdminFooter && isSuperuser
      ? [
          {
            id: 'allowed-users',
            label: 'Superuser',
            href: '/superuser',
            icon: <SettingsIcon fontSize="small" />,
            active: isSelected('/superuser', undefined, activePath),
            preload: preloadRoute
              ? (): Promise<void> | null => preloadRoute('/superuser')
              : undefined,
          },
        ]
      : []),
  ];
  const baseSections: SidebarSection[] = [
    {
      title: 'Overview',
      items: pickNavItems(filteredNav, ['reports'], activePath, preloadRoute),
    },
    {
      title: 'Testing',
      items: pickNavItems(
        filteredNav,
        ['execution-runs', 'suites', 'tests'],
        activePath,
        preloadRoute,
      ),
    },
    ...(!deliveryEnabled
      ? []
      : [
          {
            title: 'Delivery',
            items: pickNavItems(filteredNav, ['releases', 'features'], activePath, preloadRoute),
          },
        ]),
    ...(!codeEnabled
      ? []
      : [
          {
            title: 'Code',
            items: pickNavItems(
              filteredNav,
              ['coverage-runs', 'coverage-directories', 'coverage-files'],
              activePath,
              preloadRoute,
            ),
          },
        ]),
    ...(!operationsEnabled
      ? []
      : [
          {
            title: 'Operations',
            items: pickNavItems(
              filteredNav,
              ['pipelines', 'stages', 'jobs'],
              activePath,
              preloadRoute,
            ),
          },
        ]),
  ];
  const filteredSections = baseSections.filter((section) => {
    if (section.items.length === 0) return false;
    return true;
  });
  return { sections: filteredSections, preFooterItems: [feedbackItem], footerItems };
};
